import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/baseQuery';
import { METHODS } from 'config';

interface MeetingParticipant {
  _id: string;
  firstName: string;
  lastName: string;
  job: string;
  channelId?: string;
}

interface Meeting {
  _id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  creator: MeetingParticipant;
  participants: MeetingParticipant[];
  createdAt?: string;
  updatedAt?: string;
}

interface MeetingListQuery {
  page: number;
  limit: number;
  fromDate?: string;
  toDate?: string;
}

interface MeetingListResponse {
  data: Meeting[];
  count: number;
}

interface CreateMeetingInput {
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  participants?: string[];
}

interface UpdateMeetingInput {
  meetingId: string;
  title?: string;
  description?: string;
  startTime?: string;
  endTime?: string;
  participants?: string[];
}

const MeetingApi = createApi({
  reducerPath: 'meeting-api',
  baseQuery: baseQuery(),
  tagTypes: ['Meetings'],
  endpoints: (builder) => ({
    getMeetings: builder.query<MeetingListResponse, MeetingListQuery>({
      query: (params) => ({
        url: '/meeting/list',
        method: METHODS.get,
        params,
      }),
      providesTags: ['Meetings'],
    }),
    getMeeting: builder.query<{ meeting: Meeting }, string>({
      query: (meetingId) => ({
        url: `/meeting/get`,
        method: METHODS.get,
        params: { meetingId },
      }),
      providesTags: ['Meetings'],
    }),
    createMeeting: builder.mutation<{ meeting: Meeting }, CreateMeetingInput>({
      query: (body) => ({
        url: '/meeting/create',
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Meetings'],
    }),
    updateMeeting: builder.mutation<{ meeting: Meeting }, UpdateMeetingInput>({
      query: ({ meetingId, ...body }) => ({
        url: `/meeting/update`,
        method: METHODS.post,
        params: { meetingId },
        body,
      }),
      invalidatesTags: ['Meetings'],
    }),
    deleteMeeting: builder.mutation<{ success: boolean }, string>({
      query: (meetingId) => ({
        url: `/meeting/remove`,
        method: METHODS.post,
        params: { meetingId },
      }),
      invalidatesTags: ['Meetings'],
    }),
  }),
});

export const {
  useGetMeetingsQuery,
  useGetMeetingQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useDeleteMeetingMutation,
} = MeetingApi;

export { MeetingApi };
export type { Meeting, MeetingListQuery, CreateMeetingInput, UpdateMeetingInput };
